import * as React from 'react';
import { Dialog } from 'primereact/dialog';

// Define a type for the modal ref that includes openModal and closeModal methods
export type ModalHandle = {
  openModal: () => void;
  closeModal: () => void;
};

// Global counter to track how many modals are currently open
// Prevents layout shifts when multiple modals are open simultaneously
let openModalCount = 0;
let originalBodyPadding = '';

type ReusableModalProps = {
  title?: string;
  content: React.ReactNode;
  width?: string | number; // Allow width to be passed as prop
  onClose?: () => void;
  showHeader?: boolean; // Optional flag to explicitly show/hide header
};

const ReusableModal = React.forwardRef<ModalHandle, ReusableModalProps>(
  ({ title, content, width, onClose, showHeader }, ref) => {
    const [visible, setVisible] = React.useState(false);
    // Track if THIS modal instance has incremented the counter
    const hasIncrementedRef = React.useRef(false);

    // Content freezing for smooth close animation
    // Prevents showing cleared/empty state during 400ms close animation
    const frozenContentRef = React.useRef<React.ReactNode>(null);
    const frozenTitleRef = React.useRef<string | undefined>(undefined);
    const isClosingRef = React.useRef(false);

    // Update frozen content when modal is open and not closing
    React.useEffect(() => {
      if (visible && !isClosingRef.current) {
        frozenContentRef.current = content;
        frozenTitleRef.current = title;
      }
    }, [visible, content, title]);

    // Use useImperativeHandle to expose openModal and closeModal methods
    React.useImperativeHandle(ref, () => ({
      openModal: () => {
        isClosingRef.current = false;
        frozenContentRef.current = content;
        frozenTitleRef.current = title;
        setVisible(true);
      },
      closeModal: () => {
        isClosingRef.current = true;
        setVisible(false);
      },
    }));

    // Prevent layout shift when scrollbar is hidden
    // Uses reference counting to support multiple modals open simultaneously
    React.useEffect(() => {
      if (visible && !hasIncrementedRef.current) {
        // Increment counter when modal opens (only once per instance)
        openModalCount++;
        hasIncrementedRef.current = true;

        // Only set padding when first modal opens
        if (openModalCount === 1) {
          // Store original padding to restore later
          originalBodyPadding = document.body.style.paddingRight || '';

          // Calculate scrollbar width and add padding to compensate
          const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
          if (scrollbarWidth > 0) {
            document.body.style.paddingRight = `${scrollbarWidth}px`;
          }
        }
      }

      return () => {
        // Cleanup: decrement only if this instance incremented
        if (hasIncrementedRef.current && openModalCount > 0) {
          openModalCount--;
          hasIncrementedRef.current = false;

          // Only remove padding if this was the last modal
          if (openModalCount === 0) {
            document.body.style.paddingRight = originalBodyPadding;
          }
        }
      };
    }, [visible]);

    const handleClose = () => {
      // Mark as closing to freeze content during animation
      isClosingRef.current = true;
      if (onClose) {
        onClose();
      } else {
        setVisible(false);
      }
    };

    // Use frozen content during close animation to prevent showing cleared state
    const displayContent = isClosingRef.current ? frozenContentRef.current : content;
    const displayTitle = isClosingRef.current ? frozenTitleRef.current : title;

    // Determine if header should be shown
    // Show header only if explicitly set via showHeader prop
    // Default: hide header unless showHeader is true
    const shouldShowHeader = showHeader === true || (title !== undefined && onClose !== undefined);

    return (
      <Dialog
        visible={visible}
        onHide={handleClose}
        header={
          shouldShowHeader && displayTitle ? (
            <span className="font-poppins text-lg font-medium text-project-purple">
              {displayTitle}
            </span>
          ) : null
        }
        showHeader={shouldShowHeader}
        style={{
          width: width || '600px',
          maxWidth: '90vw',
        }}
        className="rounded-lg"
        headerClassName={shouldShowHeader ? 'p-5' : undefined}
        contentClassName={`overflow-y-auto ${!shouldShowHeader ? 'rounded-lg' : ''}`}
        contentStyle={{
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
          padding: shouldShowHeader ? undefined : '1rem',
          borderTopLeftRadius: !shouldShowHeader ? '0.5rem' : undefined,
          borderTopRightRadius: !shouldShowHeader ? '0.5rem' : undefined,
        }}
        dismissableMask={false}
        draggable={false}
        resizable={false}
        blockScroll={true}
        closable={!!onClose}
        transitionOptions={{
          timeout: 350 /* Synchronized with CSS animations (mask: 350ms, dialog: 350ms) */,
          classNames: 'p-dialog',
        }}
      >
        <div className={shouldShowHeader ? 'mt-2' : ''}>{displayContent}</div>
      </Dialog>
    );
  },
);

ReusableModal.displayName = 'ReusableModal';

export default ReusableModal;
